import PropTypes from 'prop-types';
import { ClassNames } from '@emotion/react';
import { PLATE, RUBBER, SELECTION, tyreState } from './diagramTheme';
import { describePosition } from './positionDescription';

const LUG_SPAN = 0.4;
const LUG_PITCH = 7.2;

const treadLugs = (width, length) => {
  const reach = length * LUG_SPAN;
  const count = Math.max(5, Math.round((reach * 2) / LUG_PITCH));
  const step = (reach * 2) / count;
  const shoulderOuter = width * 0.44;
  const shoulderInner = width * 0.2;
  const centreEdge = width * 0.13;
  const slant = step * 0.3;

  const lugs = [];
  for (let index = 0; index <= count; index += 1) {
    const y = -reach + index * step;
    lugs.push(
      <path key={`l${index}`} d={`M${-shoulderOuter} ${y + slant}L${-shoulderInner} ${y - slant}`} />,
      <path key={`r${index}`} d={`M${shoulderInner} ${y - slant}L${shoulderOuter} ${y + slant}`} />,
    );
    if (index % 2 === 0) {
      lugs.push(<path key={`c${index}`} d={`M${-centreEdge} ${y}L${centreEdge} ${y}`} />);
    }
  }
  return { lugs, step };
};

const TyreGlyph = ({ tyre, status, code, label, selected, tabbable, gradientId, nodeRef, onFocus, onSelect }) => {
  const { width, length } = tyre;
  const halfWidth = width / 2;
  const halfLength = length / 2;
  const radius = width * 0.34;
  const state = tyreState(status);
  const isEmpty = status === 'empty';
  const codeSize = Math.min(12, Math.max(8, width * 0.34));
  const { lugs, step } = treadLugs(width, length);
  const groove = width * 0.17;
  const grooveReach = length * 0.44;

  const activate = (event) => {
    event.stopPropagation();
    onSelect(tyre.position);
  };

  return (
    <ClassNames>
      {({ css }) => (
        <g
          ref={nodeRef}
          className={`tm-tyre ${css({ '--tm-tyre-color': state.color, animationDelay: `${tyre.axleIndex * 70}ms` })}`}
      data-selected={selected || undefined}
      data-empty={isEmpty || undefined}
      transform={`translate(${tyre.x} ${tyre.y})`}
      role="button"
      tabIndex={tabbable ? 0 : -1}
      aria-pressed={selected}
      aria-label={label}
      onFocus={onFocus}
      onClick={activate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activate(event);
        }
      }}
    >
      <title>{describePosition(tyre.position)}</title>
      <g className="tm-tyre__lift">
        <rect
          className="tm-tyre__contact"
          x={-halfWidth}
          y={-halfLength + 2}
          width={width}
          height={length}
          rx={radius}
        />

        {isEmpty ? (
          <>
            <rect
              className="tm-tyre__socket"
              x={-halfWidth}
              y={-halfLength}
              width={width}
              height={length}
              rx={radius}
            />
            <path
              className="tm-tyre__socket-mark"
              d={`M0 ${-halfLength * 0.34}V${halfLength * 0.34}M${-halfWidth * 0.34} 0H${halfWidth * 0.34}`}
            />
          </>
        ) : (
          <>
            <rect
              className="tm-tyre__body"
              x={-halfWidth}
              y={-halfLength}
              width={width}
              height={length}
              rx={radius}
              fill={`url(#${gradientId})`}
            />
            <rect
              className="tm-tyre__state"
              x={-halfWidth + 1.2}
              y={-halfLength + 1.2}
              width={width - 2.4}
              height={length - 2.4}
              rx={Math.max(1, radius - 1.2)}
              fill={state.color}
            />
            <g className="tm-tyre__tread" strokeWidth={step * 0.34}>{lugs}</g>
            <path
              className="tm-tyre__groove"
              strokeWidth={width * 0.055}
              d={`M${-groove} ${-grooveReach}V${grooveReach}M${groove} ${-grooveReach}V${grooveReach}`}
            />
            <rect
              className="tm-tyre__sidewall"
              x={-halfWidth + 0.6}
              y={-halfLength + 0.6}
              width={width - 1.2}
              height={length - 1.2}
              rx={radius - 0.6}
            />
          </>
        )}

        <rect
          className="tm-tyre__ring"
          x={-halfWidth}
          y={-halfLength}
          width={width}
          height={length}
          rx={radius}
        />

        <text
          className="tm-tyre__code"
          y={codeSize * 0.35}
          fontSize={codeSize}
          fill={isEmpty ? PLATE.steel : '#FFFFFF'}
        >
          {code}
        </text>

        {selected && (
          <g className="tm-tyre__reticle" stroke={SELECTION}>
            {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([sx, sy]) => {
              const cx = sx * (halfWidth + 6);
              const cy = sy * (halfLength + 6);
              const arm = Math.min(9, width * 0.3);
              return (
                <path key={`${sx}${sy}`} d={`M${cx} ${cy - sy * arm}V${cy}H${cx - sx * arm}`} />
              );
            })}
          </g>
        )}
      </g>
        </g>
      )}
    </ClassNames>
  );
};

TyreGlyph.propTypes = {
  tyre: PropTypes.object.isRequired,
  status: PropTypes.string.isRequired,
  code: PropTypes.string,
  label: PropTypes.string,
  selected: PropTypes.bool,
  tabbable: PropTypes.bool,
  gradientId: PropTypes.string.isRequired,
  nodeRef: PropTypes.func,
  onFocus: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
};

export const TyreGradients = ({ id }) => (
  <>
    <linearGradient id={id} x1="0" x2="1" y1="0" y2="0">
      <stop offset="0" stopColor={RUBBER.dark} />
      <stop offset="0.12" stopColor={RUBBER.light} />
      <stop offset="0.4" stopColor={RUBBER.base} />
      <stop offset="0.6" stopColor={RUBBER.base} />
      <stop offset="0.88" stopColor={RUBBER.light} />
      <stop offset="1" stopColor={RUBBER.dark} />
    </linearGradient>
  </>
);

TyreGradients.propTypes = { id: PropTypes.string.isRequired };

export default TyreGlyph;
